import {ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, input, signal, ViewChild} from '@angular/core';
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {GruppeId, GruppeInvitasjonSpesifikasjoner, MedlemsRolleModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {FormArray} from "@angular/forms";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {TranslateModule} from "@ngx-translate/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {MedlemImporterSkjemalinjeKomponent, MedlemImporterSkjemalinjeType} from "../medlem-importer-skjemalinje/medlem-importer-skjemalinje";
import {trimTilNull} from "felles-ng2/støttefunksjoner/strenger";
import {EnumOversettelser} from "felles-ng2/tjenester/i18n/EnumOversettelser";
import {HåndterInnlimteInvitasjoner, ParseStatus} from "./håndterInnlimteInvitasjoner";
import {GjeldendeLokale} from "felles-ng2/tjenester/i18n/GjeldendeLokale";
import {TipsKomponent} from "web-ng2/delt/komponenter/tips/tips";
import {muligeTilgangsrollerForMedlem} from "felles-ng2/modell/medlemModellHjelpere";
import {MedlemImporterSkjemaoverskrifterKomponent} from "web-ng2/moduler/medlem/importer-medlemmer/komponenter/medlem-importer-skjemaoverskrifter/medlem-importer-skjemaoverskrifter";
import {GjeldendeTidssone} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {MedlemImporterSkjema} from "web-ng2/moduler/medlem/importer-medlemmer/modale/medlem-importer-dialog/medlem-importer-dialog";
import {MatInput} from "@angular/material/input";

const debug = initDebugger('MedlemImporterSkjemaKomponent');


@Component({
    selector: 'medlem-importer-fra-regneark',
    templateUrl: './medlem-importer-fra-regneark.html',
    styleUrl: './medlem-importer-fra-regneark.scss',
    imports: [
        MatLabel,
        MatFormField,
        MatInput,
        TranslateModule,
        TipsKomponent,
        MedlemImporterSkjemaoverskrifterKomponent,
        MedlemImporterSkjemalinjeKomponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MedlemImporterFraRegnearkKomponent implements MedlemImporterSkjema{

    @ViewChild('liminnfelt') private liminnfeltElement!: ElementRef<HTMLInputElement>;

    gruppeId = input.required<GruppeId>();
    medlemsroller = input.required<MedlemsRolleModell[]>();
    
    skjema = new FormArray<MedlemImporterSkjemalinjeType>([]);
    medlemsrollerSomStreng = computed(() => this.medlemsroller().map(rolle => rolle.tittel).join(', '));

    antallValgt = signal(0);
    alleValgt = computed(()=> this.antallValgt() == this.initieltAntallValgt);
    ingenValgt = computed(()=> this.antallValgt() == 0);

    parser: HåndterInnlimteInvitasjoner | null = null;
    parseStatus = ParseStatus;

    private initieltAntallValgt = 0;

    constructor(private enumOversettelser: EnumOversettelser,
                private gjeldendeTidssone: GjeldendeTidssone,
                private gjeldendeLokale: GjeldendeLokale,
                private destroyRef: DestroyRef) {
    }
    
    tilgjengeligeTilgangsrollerSomStreng(): string {
        return muligeTilgangsrollerForMedlem()
          .map(rolleinfo => rolleinfo.tittel(this.enumOversettelser))
          .join(', ')
    }

    forventetDatoformat(): string {
        return this.gjeldendeLokale.brukersLokale().datoformat();
    }

    vedLimInn(hendelse: ClipboardEvent) {
        this.resetSkjema();

        this.parser = new HåndterInnlimteInvitasjoner(hendelse, this.medlemsroller(), this.enumOversettelser, this.gjeldendeLokale.brukersLokale());
        if (this.parser.status === ParseStatus.ok)
            this.populerSkjemaMedInnlimteInvitasjoner();

        hendelse.stopPropagation();
        hendelse.preventDefault();
        this.liminnfeltElement.nativeElement.blur();
    }

    endreAlle() {
        const erValgt = !this.alleValgt();
        this.skjema.controls
          .forEach(kontroll => MedlemImporterSkjemalinjeKomponent.setSkjemalinjeValgt(kontroll, erValgt, true));
    }

    //MedlemImporterSkjema implementasjon

    somSpesifikasjon(): GruppeInvitasjonSpesifikasjoner {
        const invitasjoner = this.skjema.value
          .filter(linje => linje.valgt)
          .map(linje => MedlemImporterSkjemalinjeKomponent.somSpesifikasjon(linje, this.gruppeId(), this.gjeldendeTidssone.gruppensTidssone(this.gruppeId())));
        return {
            gruppeId: this.gruppeId(),
            invitasjoner: invitasjoner
        };
    }

    //private hjelpemetoder

    private resetSkjema() {
        this.parser = null;
        this.initieltAntallValgt = 0;
        this.skjema.reset();
    }

    private populerSkjemaMedInnlimteInvitasjoner() {
        this.skjema.clear();
        this.parser!.hentLesteLinjer()
          .map((linje, i) => ({
              valgt: true,
              navn: linje.navn,
              epost: linje.epost!,
              mobilnummer: trimTilNull(linje.mobilnummer),
              rolle: linje.medlemsrolleEntitet,
              forespurtRolle: linje.forespurtMedlemsrolle,
              rolleStartdato: linje.medlemsrolleStartdato,
              tilgangsroller: linje.tilgangsroller,
              oppdaterMoetemal: false,
              skalSignereSomStandard: false
          }))
          .forEach(linjeverdier => {
              const skjemalinje = MedlemImporterSkjemalinjeKomponent.opprettSkjema(this.destroyRef);
              skjemalinje.setValue(linjeverdier);

              //hold antallValgt-teller i synk
              skjemalinje.controls.valgt.valueChanges
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(valgt =>
                  this.antallValgt.update(antall => antall + (valgt ? 1 : -1)));

              this.skjema.push(skjemalinje);
          });

        this.initieltAntallValgt = this.skjema.length;
        this.antallValgt.set(this.initieltAntallValgt);
    }

}
